import React, { useState, useEffect } from "react";
import {
  ArrowDownCircle,
  ArrowUpCircle,
  Users,
  TrendingUp,
  RefreshCw,
  Calendar,
  ShieldCheck,
  Mail,
  AlertTriangle,
  LifeBuoy,
  Gift,
  Coins,
  ArrowRight,
  Sliders,
  Activity,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../utils/axios";
import formatCurrency from "../helper/formatCurrency";

const AdminDashboard = () => {
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const navigate = useNavigate();

  const getDashboardData = async (isManual = false) => {
    if (isManual) setRefreshing(true);
    else setLoading(true);

    try {
      const response = await api.get("/getDashboardData");
      const data = response.data;
      setDashboardData(data?.data ?? {});
      setLastUpdated(new Date());
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    getDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto space-y-6 py-2">
        <div className="h-24 bg-white rounded-2xl animate-pulse shadow-sm border border-slate-100" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-24 bg-white rounded-2xl animate-pulse shadow-sm border border-slate-100" />
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="h-64 bg-white rounded-2xl animate-pulse shadow-sm border border-slate-100" />
          <div className="h-64 bg-white rounded-2xl animate-pulse shadow-sm border border-slate-100" />
        </div>
      </div>
    );
  }

  const netRevenue = (dashboardData?.totalDeposits ?? 0) - (dashboardData?.totalWithdrawals ?? 0);
  const todayNet = (dashboardData?.todayDeposits ?? 0) - (dashboardData?.todayWithdrawals ?? 0);

  return (
    <div className="max-w-7xl mx-auto space-y-6 pb-12">
      {/* ── TOP HEADER & STATUS BAR ── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-5 md:p-6 rounded-2xl border border-slate-200/80 shadow-xs">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight">
              Admin Command Center
            </h1>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              Live
            </span>
          </div>
          <p className="text-xs md:text-sm text-slate-500 mt-1">
            Real-time sweepstakes gaming liquidity, player activity, and compliance operations
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-200/80 px-3.5 py-2 rounded-xl">
            <Calendar size={14} className="text-slate-400" />
            <span>Updated: {lastUpdated.toLocaleTimeString()}</span>
          </div>

          <button
            onClick={() => getDashboardData(true)}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold rounded-xl transition-all shadow-sm hover:shadow cursor-pointer disabled:opacity-60"
          >
            <RefreshCw size={14} className={refreshing ? "animate-spin text-amber-400" : ""} />
            {refreshing ? "Syncing..." : "Refresh"}
          </button>
        </div>
      </div>

      {/* ── ACTION REQUIRED / COMPLIANCE TICKER ── */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
        <div
          onClick={() => navigate("/kyc-verification")}
          className="p-3.5 rounded-2xl bg-white border border-slate-200/80 hover:border-amber-300 hover:shadow-md transition-all cursor-pointer flex items-center justify-between group"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-amber-600 flex items-center justify-center font-bold">
              <ShieldCheck size={20} />
            </div>
            <div>
              <div className="text-[11px] font-bold uppercase text-slate-400 tracking-wider">Pending KYC</div>
              <div className="text-lg font-extrabold text-slate-800">
                {dashboardData?.pendingKycCount ?? 0} <span className="text-xs font-normal text-slate-500">requests</span>
              </div>
            </div>
          </div>
          <ArrowRight size={14} className="text-slate-300 group-hover:text-amber-500 group-hover:translate-x-1 transition-all" />
        </div>

        <div
          onClick={() => navigate("/amoe-management")}
          className="p-3.5 rounded-2xl bg-white border border-slate-200/80 hover:border-sky-300 hover:shadow-md transition-all cursor-pointer flex items-center justify-between group"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-sky-500/10 text-sky-600 flex items-center justify-center font-bold">
              <Mail size={20} />
            </div>
            <div>
              <div className="text-[11px] font-bold uppercase text-slate-400 tracking-wider">AMOE Mail-Ins</div>
              <div className="text-lg font-extrabold text-slate-800">
                {dashboardData?.pendingAmoeCount ?? 0} <span className="text-xs font-normal text-slate-500">entries</span>
              </div>
            </div>
          </div>
          <ArrowRight size={14} className="text-slate-300 group-hover:text-sky-500 group-hover:translate-x-1 transition-all" />
        </div>

        <div
          onClick={() => navigate("/self-exclusion")}
          className="p-3.5 rounded-2xl bg-white border border-slate-200/80 hover:border-rose-300 hover:shadow-md transition-all cursor-pointer flex items-center justify-between group"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-rose-500/10 text-rose-600 flex items-center justify-center font-bold">
              <AlertTriangle size={20} />
            </div>
            <div>
              <div className="text-[11px] font-bold uppercase text-slate-400 tracking-wider">Self-Excluded</div>
              <div className="text-lg font-extrabold text-slate-800">
                {dashboardData?.activeSelfExclusions ?? 0} <span className="text-xs font-normal text-slate-500">active</span>
              </div>
            </div>
          </div>
          <ArrowRight size={14} className="text-slate-300 group-hover:text-rose-500 group-hover:translate-x-1 transition-all" />
        </div>

        <div
          onClick={() => navigate("/pending-issues")}
          className="p-3.5 rounded-2xl bg-white border border-slate-200/80 hover:border-indigo-300 hover:shadow-md transition-all cursor-pointer flex items-center justify-between group"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-500/10 text-indigo-600 flex items-center justify-center font-bold">
              <LifeBuoy size={20} />
            </div>
            <div>
              <div className="text-[11px] font-bold uppercase text-slate-400 tracking-wider">Support Tickets</div>
              <div className="text-lg font-extrabold text-slate-800">
                {dashboardData?.pendingTicketsCount ?? 0} <span className="text-xs font-normal text-slate-500">open</span>
              </div>
            </div>
          </div>
          <ArrowRight size={14} className="text-slate-300 group-hover:text-indigo-500 group-hover:translate-x-1 transition-all" />
        </div>
      </div>

      {/* ── DUAL CURRENCY COMMAND VAULT (GC & SC) ── */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-950 rounded-2xl p-6 text-white shadow-xl border border-slate-800">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-5 border-b border-slate-800/80 gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-400">
              <Coins size={22} />
            </div>
            <div>
              <h2 className="text-lg font-extrabold text-white tracking-wide">
                Virtual Currency Liquidity & Gaming Turnover
              </h2>
              <p className="text-xs text-slate-400">
                Live circulating balances and total gameplay wagering across dual currencies
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-amber-500/15 text-amber-300 border border-amber-500/30">
              Gold Coins (Entertainment)
            </span>
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-cyan-500/15 text-cyan-300 border border-cyan-500/30">
              Sweepstakes Coins (Prize)
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
          {/* Gold Coins (GC) Card */}
          <div className="rounded-xl bg-slate-800/50 border border-slate-700/60 p-5 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/5 rounded-full blur-2xl pointer-events-none" />
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="text-xl">🪙</span>
                <span className="text-xs font-bold uppercase tracking-wider text-amber-400">
                  Outstanding Gold Coins (GC)
                </span>
              </div>
              <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded bg-amber-400/10 text-amber-300">
                Play Balance
              </span>
            </div>

            <div className="text-3xl font-black text-amber-300 tracking-tight">
              {Number(dashboardData?.outstandingGcBalance || 0).toLocaleString()}{" "}
              <span className="text-sm font-semibold text-amber-400/70">GC</span>
            </div>
            <p className="text-xs text-slate-400 mt-1">Total entertainment currency in player wallets</p>

            <div className="mt-5 pt-4 border-t border-slate-700/60 grid grid-cols-2 gap-4">
              <div>
                <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Total GC Wagered</span>
                <div className="text-sm font-bold text-slate-200 mt-0.5">
                  {Number(dashboardData?.totalGcWagered || 0).toLocaleString()} GC
                </div>
              </div>
              <div>
                <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Total GC Won</span>
                <div className="text-sm font-bold text-slate-200 mt-0.5">
                  {Number(dashboardData?.totalGcWon || 0).toLocaleString()} GC
                </div>
              </div>
            </div>
          </div>

          {/* Sweepstakes Coins (SC) Card */}
          <div className="rounded-xl bg-slate-800/50 border border-slate-700/60 p-5 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-500/5 rounded-full blur-2xl pointer-events-none" />
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="text-xl">✨</span>
                <span className="text-xs font-bold uppercase tracking-wider text-cyan-400">
                  Outstanding Sweepstakes Coins (SC)
                </span>
              </div>
              <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded bg-cyan-400/10 text-cyan-300">
                Prize Liability
              </span>
            </div>

            <div className="text-3xl font-black text-cyan-300 tracking-tight">
              {Number(dashboardData?.outstandingScBalance || 0).toFixed(2)}{" "}
              <span className="text-sm font-semibold text-cyan-400/70">SC</span>
            </div>
            <p className="text-xs text-slate-400 mt-1">
              Redeemable prize coins currently held across eligible players
            </p>

            <div className="mt-5 pt-4 border-t border-slate-700/60 grid grid-cols-2 gap-4">
              <div>
                <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Total SC Wagered</span>
                <div className="text-sm font-bold text-slate-200 mt-0.5">
                  {Number(dashboardData?.totalScWagered || 0).toFixed(2)} SC
                </div>
              </div>
              <div>
                <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Total SC Won</span>
                <div className="text-sm font-bold text-slate-200 mt-0.5">
                  {Number(dashboardData?.totalScWon || 0).toFixed(2)} SC
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── PLATFORM FINANCIAL OVERVIEW (REPLACED AGENTS WITH CLEAN METRICS) ── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {/* Gross Coin Purchases */}
        <div className="bg-white p-6 rounded-2xl border border-slate-200/80 shadow-xs flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
              Total Coin Purchases (Inflow)
            </span>
            <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold">
              <ArrowUpCircle size={20} />
            </div>
          </div>
          <div className="mt-3">
            <div className="text-3xl font-extrabold text-slate-900 tracking-tight">
              {formatCurrency(dashboardData?.totalDeposits)}
            </div>
            <div className="flex items-center gap-2 mt-2 text-xs">
              <span className="text-emerald-700 font-bold bg-emerald-50 px-2 py-0.5 rounded">
                {dashboardData?.deposits?.approvedCount ?? 0} approved
              </span>
              <span className="text-slate-400">
                ${Number(dashboardData?.deposits?.approvedAmount ?? 0).toLocaleString()} settled
              </span>
            </div>
          </div>
          <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
            <span>Pending settlement:</span>
            <span className="font-semibold text-amber-600">
              {dashboardData?.deposits?.pendingCount ?? 0} orders ({formatCurrency(dashboardData?.deposits?.pendingAmount)})
            </span>
          </div>
        </div>

        {/* Prize Redemptions */}
        <div className="bg-white p-6 rounded-2xl border border-slate-200/80 shadow-xs flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
              Prize Redemptions (Outflow)
            </span>
            <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center font-bold">
              <ArrowDownCircle size={20} />
            </div>
          </div>
          <div className="mt-3">
            <div className="text-3xl font-extrabold text-slate-900 tracking-tight">
              {formatCurrency(dashboardData?.totalWithdrawals)}
            </div>
            <div className="flex items-center gap-2 mt-2 text-xs">
              <span className="text-rose-700 font-bold bg-rose-50 px-2 py-0.5 rounded">
                {dashboardData?.withdrawals?.approvedCount ?? 0} approved
              </span>
              <span className="text-slate-400">
                ${Number(dashboardData?.withdrawals?.approvedAmount ?? 0).toLocaleString()} paid
              </span>
            </div>
          </div>
          <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
            <span>Pending review:</span>
            <span className="font-semibold text-rose-600">
              {dashboardData?.withdrawals?.pendingCount ?? 0} requests ({formatCurrency(dashboardData?.withdrawals?.pendingAmount)})
            </span>
          </div>
        </div>

        {/* Net Platform Gaming Margin */}
        <div className="bg-white p-6 rounded-2xl border border-slate-200/80 shadow-xs flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
              Net Platform Margin (GGR)
            </span>
            <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center font-bold">
              <TrendingUp size={20} />
            </div>
          </div>
          <div className="mt-3">
            <div className={`text-3xl font-extrabold tracking-tight ${netRevenue >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
              {formatCurrency(netRevenue)}
            </div>
            <div className="flex items-center gap-2 mt-2 text-xs">
              <span className="text-slate-500">
                Purchases minus Processed Redemptions
              </span>
            </div>
          </div>
          <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
            <span>Player SC Liability:</span>
            <span className="font-semibold text-slate-800">
              ${Number(dashboardData?.outstandingScBalance || 0).toFixed(2)} SC
            </span>
          </div>
        </div>
      </div>

      {/* ── TODAY'S REAL-TIME ACTIVITY ── */}
      <div className="bg-white rounded-2xl p-6 border border-slate-200/80 shadow-xs">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-slate-900 text-amber-400 flex items-center justify-center font-bold">
            <Activity size={20} />
          </div>
          <div>
            <h2 className="text-lg font-extrabold text-slate-900">Today's Real-Time Performance</h2>
            <p className="text-xs text-slate-500">Summary of today's deposits, withdrawals, and registrations</p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
            <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Today's Purchases</div>
            <div className="text-xl font-extrabold text-slate-900 mt-1">
              {formatCurrency(dashboardData?.todayDeposits)}
            </div>
            <span className="text-[10px] text-emerald-600 font-semibold mt-1 inline-block">Daily gross sales</span>
          </div>

          <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
            <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Today's Redemptions</div>
            <div className="text-xl font-extrabold text-slate-900 mt-1">
              {formatCurrency(dashboardData?.todayWithdrawals)}
            </div>
            <span className="text-[10px] text-rose-500 font-semibold mt-1 inline-block">Daily prize claims</span>
          </div>

          <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
            <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Today's Net Margin</div>
            <div className={`text-xl font-extrabold mt-1 ${todayNet >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
              {formatCurrency(todayNet)}
            </div>
            <span className="text-[10px] text-slate-500 font-semibold mt-1 inline-block">Inflow - Outflow</span>
          </div>

          <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
            <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">New Players Today</div>
            <div className="text-xl font-extrabold text-slate-900 mt-1">
              {dashboardData?.todayUsers ?? 0}
            </div>
            <span className="text-[10px] text-indigo-600 font-semibold mt-1 inline-block">Registered accounts</span>
          </div>
        </div>
      </div>

      {/* ── TRANSACTION PIPELINE SUMMARY (DEPOSITS & WITHDRAWALS) ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Coin Purchases Breakdown */}
        <div className="bg-white p-6 rounded-2xl border border-slate-200/80 shadow-xs">
          <div className="flex items-center justify-between pb-4 border-b border-slate-100">
            <div>
              <h3 className="font-extrabold text-slate-900 text-sm">Coin Purchases Status</h3>
              <p className="text-xs text-slate-400">Transaction pipeline breakdown</p>
            </div>
            <button
              onClick={() => navigate("/recharge-list")}
              className="text-xs font-semibold text-amber-600 hover:text-amber-700 flex items-center gap-1"
            >
              View list <ArrowRight size={12} />
            </button>
          </div>

          <div className="space-y-4 mt-4">
            <div className="flex items-center justify-between p-3 rounded-xl bg-emerald-50/50 border border-emerald-100">
              <div className="flex items-center gap-2.5">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                <span className="text-xs font-bold text-slate-800">Approved Purchases</span>
              </div>
              <div className="text-right">
                <div className="text-xs font-bold text-slate-900">
                  {formatCurrency(dashboardData?.deposits?.approvedAmount)}
                </div>
                <div className="text-[10px] text-slate-500">
                  {dashboardData?.deposits?.approvedCount ?? 0} orders
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 rounded-xl bg-amber-50/50 border border-amber-100">
              <div className="flex items-center gap-2.5">
                <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                <span className="text-xs font-bold text-slate-800">Pending Settlement</span>
              </div>
              <div className="text-right">
                <div className="text-xs font-bold text-slate-900">
                  {formatCurrency(dashboardData?.deposits?.pendingAmount)}
                </div>
                <div className="text-[10px] text-slate-500">
                  {dashboardData?.deposits?.pendingCount ?? 0} orders
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-100">
              <div className="flex items-center gap-2.5">
                <span className="w-2.5 h-2.5 rounded-full bg-slate-400" />
                <span className="text-xs font-bold text-slate-800">Rejected / Failed</span>
              </div>
              <div className="text-right">
                <div className="text-xs font-bold text-slate-900">
                  {formatCurrency(dashboardData?.deposits?.rejectedAmount)}
                </div>
                <div className="text-[10px] text-slate-500">
                  {dashboardData?.deposits?.rejectedCount ?? 0} orders
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Prize Redemptions Breakdown */}
        <div className="bg-white p-6 rounded-2xl border border-slate-200/80 shadow-xs">
          <div className="flex items-center justify-between pb-4 border-b border-slate-100">
            <div>
              <h3 className="font-extrabold text-slate-900 text-sm">Prize Redemptions Status</h3>
              <p className="text-xs text-slate-400">Withdrawal payout review pipeline</p>
            </div>
            <button
              onClick={() => navigate("/withdrawal-list")}
              className="text-xs font-semibold text-rose-600 hover:text-rose-700 flex items-center gap-1"
            >
              View list <ArrowRight size={12} />
            </button>
          </div>

          <div className="space-y-4 mt-4">
            <div className="flex items-center justify-between p-3 rounded-xl bg-emerald-50/50 border border-emerald-100">
              <div className="flex items-center gap-2.5">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                <span className="text-xs font-bold text-slate-800">Approved & Paid</span>
              </div>
              <div className="text-right">
                <div className="text-xs font-bold text-slate-900">
                  {formatCurrency(dashboardData?.withdrawals?.approvedAmount)}
                </div>
                <div className="text-[10px] text-slate-500">
                  {dashboardData?.withdrawals?.approvedCount ?? 0} payouts
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 rounded-xl bg-rose-50/50 border border-rose-100">
              <div className="flex items-center gap-2.5">
                <span className="w-2.5 h-2.5 rounded-full bg-rose-500 animate-pulse" />
                <span className="text-xs font-bold text-slate-800">Pending Verification</span>
              </div>
              <div className="text-right">
                <div className="text-xs font-bold text-rose-700">
                  {formatCurrency(dashboardData?.withdrawals?.pendingAmount)}
                </div>
                <div className="text-[10px] text-slate-500">
                  {dashboardData?.withdrawals?.pendingCount ?? 0} requests
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-100">
              <div className="flex items-center gap-2.5">
                <span className="w-2.5 h-2.5 rounded-full bg-slate-400" />
                <span className="text-xs font-bold text-slate-800">Declined / Blocked</span>
              </div>
              <div className="text-right">
                <div className="text-xs font-bold text-slate-900">
                  {formatCurrency(dashboardData?.withdrawals?.rejectedAmount)}
                </div>
                <div className="text-[10px] text-slate-500">
                  {dashboardData?.withdrawals?.rejectedCount ?? 0} requests
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── QUICK ACTION SHORTCUTS ── */}
      <div className="bg-slate-900 rounded-2xl p-6 text-white border border-slate-800 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h3 className="font-extrabold text-base text-white">Management Quick Actions</h3>
          <p className="text-xs text-slate-400 mt-0.5">Jump directly to frequent administrative tools</p>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          <button
            onClick={() => navigate("/bonus-management")}
            className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-xs font-semibold text-slate-200 hover:text-white border border-slate-700/80 transition-all cursor-pointer"
          >
            <Gift size={14} className="text-amber-400" />
            Create Bonus
          </button>

          <button
            onClick={() => navigate("/bonus-and-panelty")}
            className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-xs font-semibold text-slate-200 hover:text-white border border-slate-700/80 transition-all cursor-pointer"
          >
            <Sliders size={14} className="text-emerald-400" />
            Adjust Balances
          </button>

          <button
            onClick={() => navigate("/settings/states")}
            className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-xs font-semibold text-slate-200 hover:text-white border border-slate-700/80 transition-all cursor-pointer"
          >
            <ShieldCheck size={14} className="text-sky-400" />
            Geo-Restrictions
          </button>

          <button
            onClick={() => navigate("/users")}
            className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-xs font-bold text-slate-950 transition-all cursor-pointer shadow-md shadow-amber-500/10"
          >
            <Users size={14} />
            View Players
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
